import { useEffect, useRef, useState } from "react";
import { Axios } from "../../../Api/axios";
import {
  ADMIN_GALLERY,
  ADMIN_GALLERY_BY_ID,
  ADMIN_GALLERY_SETTINGS,
  ADMIN_GALLERY_REORDER,
  GALLERY_PHOTO_URL,
} from "../../../Api/Api";
import "../dashboard.css";
import "./gallery.css";

const PRESETS = [
  { label: "3 ث",  value: 3000 },
  { label: "4.5 ث", value: 4500 },
  { label: "6 ث",  value: 6000 },
  { label: "10 ث", value: 10000 },
];

/* ─── Delete confirm modal ─── */
function DeleteModal({ image, onConfirm, onCancel, deleting }) {
  return (
    <div className="gl-overlay" onClick={!deleting ? onCancel : undefined}>
      <div className="gl-confirm" onClick={(e) => e.stopPropagation()}>
        <div className="gl-confirm-icon">
          <svg width="26" height="26" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </div>
        <h3>حذف الصورة</h3>
        <img src={GALLERY_PHOTO_URL(image.photo)} alt="" className="gl-confirm-thumb" />
        {image.title && <p>{image.title}</p>}
        <p style={{ fontSize: "0.78rem", color: "#ef4444" }}>لا يمكن التراجع عن هذا الإجراء</p>
        <div className="gl-confirm-actions">
          <button className="gl-cancel-btn" onClick={onCancel} disabled={deleting}>إلغاء</button>
          <button className="gl-delete-btn" onClick={onConfirm} disabled={deleting}>
            {deleting ? "جاري الحذف..." : "حذف"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Single image card ─── */
function ImageCard({ image, index, total, onDelete, onMoveUp, onMoveDown, onTitleSave }) {
  const [title,   setTitle]   = useState(image.title || "");
  const [saving,  setSaving]  = useState(false);

  async function saveTitle() {
    if (title === (image.title || "")) return;
    setSaving(true);
    try {
      await Axios.put(ADMIN_GALLERY_BY_ID(image.id), { title });
      onTitleSave(image.id, title);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="gl-img-card">
      <img
        src={GALLERY_PHOTO_URL(image.photo)}
        alt={image.title || ""}
        className="gl-img-thumb"
        loading="lazy"
      />
      <span className="gl-img-order">#{index + 1}</span>

      <div className="gl-img-body">
        <input
          type="text"
          className="gl-img-title-input"
          placeholder="عنوان الصورة (اختياري)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => e.key === "Enter" && saveTitle()}
        />
        <div className="gl-img-actions">
          <div className="gl-order-btns">
            <button className="gl-order-btn" title="تقديم" onClick={() => onMoveUp(index)} disabled={index === 0}>
              ↑
            </button>
            <button className="gl-order-btn" title="تأخير" onClick={() => onMoveDown(index)} disabled={index === total - 1}>
              ↓
            </button>
          </div>
          {title !== (image.title || "") && (
            <button className="gl-title-save-btn" onClick={saveTitle} disabled={saving}>
              {saving ? "..." : "حفظ"}
            </button>
          )}
          <button className="gl-del-btn" onClick={() => onDelete(image)} title="حذف">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ─── */
export default function Gallery() {
  const [images,       setImages]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [intervalMs,   setIntervalMs]   = useState(4500);
  const [intervalEdit, setIntervalEdit] = useState(4500);
  const [savingInt,    setSavingInt]    = useState(false);
  const [savedOk,      setSavedOk]      = useState(false);

  /* upload form */
  const [file,       setFile]       = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [newTitle,   setNewTitle]   = useState("");
  const [uploading,  setUploading]  = useState(false);
  const [uploadErr,  setUploadErr]  = useState("");
  const [dragOver,   setDragOver]   = useState(false);
  const fileRef = useRef(null);

  /* delete modal */
  const [pendingDel, setPendingDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  /* load */
  useEffect(() => {
    Axios.get(ADMIN_GALLERY)
      .then((res) => {
        setImages(res.data.images || []);
        const ms = res.data.settings?.interval_ms || 4500;
        setIntervalMs(ms);
        setIntervalEdit(ms);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* ─── Upload ─── */
  function pickFile(f) {
    if (!f || !f.type.startsWith("image/")) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploadErr("");
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      if (newTitle.trim()) fd.append("title", newTitle.trim());
      const res = await Axios.post(ADMIN_GALLERY, fd);
      setImages((prev) => [...prev, res.data.image]);
      setFile(null);
      setPreview(null);
      setNewTitle("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setUploadErr(err?.response?.data?.message || "فشل رفع الصورة");
    } finally {
      setUploading(false);
    }
  }

  /* ─── Reorder ─── */
  async function move(index, direction) {
    const next = [...images];
    const swap = index + direction;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    const reordered = next.map((img, i) => ({ ...img, sort_order: i }));
    setImages(reordered);
    try {
      await Axios.put(ADMIN_GALLERY_REORDER, {
        order: reordered.map((img) => ({ id: img.id, sort_order: img.sort_order })),
      });
    } catch {}
  }

  /* ─── Delete ─── */
  async function confirmDelete() {
    if (!pendingDel) return;
    setDeleting(true);
    try {
      await Axios.delete(ADMIN_GALLERY_BY_ID(pendingDel.id));
      setImages((prev) => prev.filter((img) => img.id !== pendingDel.id));
      setPendingDel(null);
    } catch {}
    finally { setDeleting(false); }
  }

  /* ─── Title save callback ─── */
  function handleTitleSave(id, title) {
    setImages((prev) => prev.map((img) => img.id === id ? { ...img, title } : img));
  }

  /* ─── Settings ─── */
  async function saveInterval() {
    setSavingInt(true);
    try {
      await Axios.put(ADMIN_GALLERY_SETTINGS, { interval_ms: intervalEdit });
      setIntervalMs(intervalEdit);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } finally { setSavingInt(false); }
  }

  return (
    <div className="dash-page gl-page">
      {/* Header */}
      <div className="dash-page-header">
        <h1 className="dash-page-title">معرض صور الصفحة الرئيسية</h1>
      </div>

      {/* ── Top row: upload + settings side by side ── */}
      <div className="gl-top-row">

      {/* ── Upload card ── */}
      <div className="gl-upload-card">
        <div className="gl-section-head">
          <div className="gl-section-icon">
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </div>
          <div>
            <h3 className="gl-section-title">إضافة صورة جديدة</h3>
            <p className="gl-section-sub">تُعرض هذه الصور في معرض الصفحة الرئيسية للموقع</p>
          </div>
        </div>

        <div className="gl-upload-body">
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => pickFile(e.target.files[0])} />

          {/* Drop zone / file chip */}
          {file ? (
            <div className="gl-file-chip">
              <img src={preview} alt="" />
              <div className="gl-chip-info">
                <span className="gl-chip-name">{file.name}</span>
                <span className="gl-chip-size">{(file.size / 1024).toFixed(0)} KB</span>
              </div>
              <button className="gl-chip-remove" onClick={() => { setFile(null); setPreview(null); }}>✕</button>
            </div>
          ) : (
            <div
              className={`gl-dropzone${dragOver ? " drag-over" : ""}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]); }}
            >
              <span className="gl-dropzone-icon">🖼️</span>
              <p>اضغط أو اسحب صورة هنا</p>
              <span className="gl-dz-sub">PNG، JPG، WEBP — حتى 10 ميغابايت</span>
            </div>
          )}

          {/* Title + submit */}
          <div className="gl-upload-footer">
            <div className="gl-title-field">
              <label className="gl-label">
                عنوان الصورة
                <span className="gl-label-optional">(اختياري)</span>
              </label>
              <input type="text" className="gl-title-input"
                placeholder="مثال: مرحباً بك في المعهد..."
                value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            </div>
            <button className="gl-submit-btn" onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? "جاري الرفع..." : "رفع الصورة"}
            </button>
          </div>
        </div>

        {uploadErr && <div className="gl-error" style={{ marginTop: 12 }}>{uploadErr}</div>}
      </div>

      {/* ── Settings card ── */}
      <div className="gl-settings-card">
        <div className="gl-section-head">
          <div className="gl-section-icon">
            <svg width="17" height="17" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
            </svg>
          </div>
          <div>
            <h3 className="gl-section-title">إعدادات العرض</h3>
            <p className="gl-section-sub">مدة ظهور كل صورة</p>
          </div>
        </div>

        {/* big number display */}
        <div className="gl-big-interval">
          <button className="gl-int-step" onClick={() => setIntervalEdit(Math.max(1000, intervalEdit - 500))}>−</button>
          <div className="gl-int-display">
            <span className="gl-int-num">{(intervalEdit / 1000).toFixed(1)}</span>
            <span className="gl-int-unit">ثانية</span>
          </div>
          <button className="gl-int-step" onClick={() => setIntervalEdit(Math.min(30000, intervalEdit + 500))}>+</button>
        </div>

        <div className="gl-preset-row" style={{ justifyContent: "center" }}>
          {PRESETS.map((p) => (
            <button key={p.value}
              className={`gl-preset${intervalEdit === p.value ? " active" : ""}`}
              onClick={() => setIntervalEdit(p.value)}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="gl-settings-save-row">
          {savedOk && <span className="gl-save-ok">✓ تم الحفظ</span>}
          {intervalMs !== intervalEdit && !savingInt && (
            <span className="gl-current-val">الحالي: {intervalMs / 1000} ث</span>
          )}
          <button className="gl-save-btn" onClick={saveInterval}
            disabled={savingInt || intervalEdit === intervalMs}>
            {savingInt ? "..." : "حفظ"}
          </button>
        </div>
      </div>

      </div>{/* end gl-top-row */}

      {/* ── Image grid ── */}
      <div className="gl-grid-card">
        <div className="gl-grid-header">
          <div className="gl-section-head" style={{ marginBottom: 0 }}>
            <div className="gl-section-icon">
              <svg width="17" height="17" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
              </svg>
            </div>
            <h3 className="gl-section-title">صور المعرض</h3>
          </div>
          <span className="gl-count-badge">{images.length} صورة</span>
        </div>

        {loading ? (
          <div className="gl-empty">
            <span className="gl-empty-icon">⏳</span>
            <p>جاري التحميل...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="gl-empty">
            <span className="gl-empty-icon">🖼️</span>
            <p>لا توجد صور في المعرض بعد</p>
          </div>
        ) : (
          <div className="gl-grid">
            {images.map((img, i) => (
              <ImageCard
                key={img.id}
                image={img}
                index={i}
                total={images.length}
                onDelete={setPendingDel}
                onMoveUp={(idx) => move(idx, -1)}
                onMoveDown={(idx) => move(idx, 1)}
                onTitleSave={handleTitleSave}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete modal */}
      {pendingDel && (
        <DeleteModal
          image={pendingDel}
          onConfirm={confirmDelete}
          onCancel={() => !deleting && setPendingDel(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
